// src/lib/address.ts
// Keyless shipping-address autocomplete for the checkout form.
//
// Provider: Photon (https://photon.komoot.io) — free, no API key, CORS-enabled,
// OpenStreetMap-backed. Good enough for a low-traffic store and ships TODAY with
// zero billing setup. To upgrade to Google Places later, swap searchAddresses'
// body to hit a /api/places proxy (keeps the API key server-side); the
// AddressSuggestion shape the checkout consumes stays identical.

export interface AddressSuggestion {
  label: string;   // human-readable single line for the dropdown
  line1: string;   // street address (house number + street)
  city: string;
  state: string;   // 2-letter code (mapped from full name)
  zip: string;
  country: string; // 2-letter ISO code
}

// Full state/province name -> 2-letter code. Photon returns full names; Square
// + our shipping table expect codes.
const US_STATES: Record<string, string> = {
  Alabama: 'AL', Alaska: 'AK', Arizona: 'AZ', Arkansas: 'AR', California: 'CA',
  Colorado: 'CO', Connecticut: 'CT', Delaware: 'DE', 'District of Columbia': 'DC',
  Florida: 'FL', Georgia: 'GA', Hawaii: 'HI', Idaho: 'ID', Illinois: 'IL',
  Indiana: 'IN', Iowa: 'IA', Kansas: 'KS', Kentucky: 'KY', Louisiana: 'LA',
  Maine: 'ME', Maryland: 'MD', Massachusetts: 'MA', Michigan: 'MI', Minnesota: 'MN',
  Mississippi: 'MS', Missouri: 'MO', Montana: 'MT', Nebraska: 'NE', Nevada: 'NV',
  'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
  'North Carolina': 'NC', 'North Dakota': 'ND', Ohio: 'OH', Oklahoma: 'OK',
  Oregon: 'OR', Pennsylvania: 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', Tennessee: 'TN', Texas: 'TX', Utah: 'UT', Vermont: 'VT',
  Virginia: 'VA', Washington: 'WA', 'West Virginia': 'WV', Wisconsin: 'WI',
  Wyoming: 'WY', 'Puerto Rico': 'PR',
};

const CA_PROVINCES: Record<string, string> = {
  Alberta: 'AB', 'British Columbia': 'BC', Manitoba: 'MB', 'New Brunswick': 'NB',
  'Newfoundland and Labrador': 'NL', 'Nova Scotia': 'NS', Ontario: 'ON',
  'Prince Edward Island': 'PE', Quebec: 'QC', Québec: 'QC', Saskatchewan: 'SK',
  'Northwest Territories': 'NT', Nunavut: 'NU', Yukon: 'YT',
};

function toStateCode(name: string, country: string): string {
  if (!name) return '';
  if (/^[A-Za-z]{2}$/.test(name)) return name.toUpperCase();
  const map = country === 'CA' ? CA_PROVINCES : US_STATES;
  return map[name] || name;
}

// searchAddresses — debounced type-ahead lookup. Returns up to 6 suggestions
// restricted to the selected country. Never throws (returns [] on any failure).
export async function searchAddresses(query: string, country = 'US'): Promise<AddressSuggestion[]> {
  const q = (query || '').trim();
  if (q.length < 3) return [];
  try {
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&lang=en`;
    const r = await fetch(url);
    if (!r.ok) return [];
    const data = await r.json();
    const feats: any[] = Array.isArray(data?.features) ? data.features : [];
    const out: AddressSuggestion[] = [];
    const seen = new Set<string>();
    for (const f of feats) {
      const p = f?.properties || {};
      const cc = String(p.countrycode || '').toUpperCase();
      if (cc && country && cc !== country) continue; // keep selected country only
      const street = p.street || p.name || '';
      const line1 = [p.housenumber, street].filter(Boolean).join(' ').trim() || p.name || '';
      if (!line1) continue;
      const city = p.city || p.town || p.village || p.municipality || p.county || '';
      const state = toStateCode(p.state || '', cc || country);
      const zip = p.postcode || '';
      const label = [line1, city, state, zip].filter(Boolean).join(', ');
      if (seen.has(label)) continue;
      seen.add(label);
      out.push({ label, line1, city, state, zip, country: cc || country });
      if (out.length >= 6) break;
    }
    return out;
  } catch {
    return [];
  }
}
